// Admin orders (/admin/orders) — cross-user order list with status/tier filters.
// Amounts are the FROZEN order amounts (never current pricing). Sales are closed
// (DECISIONS D36); this is the order history.

import { useState } from "react";
import { Link } from "react-router-dom";
import { dataClass } from "@ahcut/ui/recipes";
import { Badge } from "@/ui";
import { cn } from "@/lib/cn";
import { PageHeader } from "@/components/PageHeader";
import { Loading, ErrorState, EmptyState } from "@/components/States";
import { Pagination } from "@/components/Pagination";
import { FilterSelect } from "@/components/FilterSelect";
import { useAsync } from "@/lib/useAsync";
import { listAdminOrders } from "@/lib/admin";
import {
  formatDateTime,
  formatRupiah,
  orderStatusLabel,
  orderStatusOptions,
  orderStatusTone,
  tierLabel,
  tierOptions,
} from "@/lib/format";
import type { AdminOrder, AdminOrderStatus, Paged, PaidTierId } from "@/lib/types";
import { AdminPanel } from "@/components/admin/AdminPanel";
import { DataCell, DataRow, DataTable, FilterBar, type DataColumn } from "@/components/admin/DataTable";
import { dataCellClass } from "@/components/admin/classes";

const PAGE = 25;

const STATUS_OPTIONS = orderStatusOptions();
const TIER_OPTIONS = tierOptions({ paidOnly: true, withAll: true });

const COLUMNS: readonly DataColumn[] = [
  { label: "Waktu" },
  { label: "Pengguna" },
  { label: "Tier" },
  { label: "Jumlah", align: "end" },
  { label: "Status" },
  { label: "Detail" },
];

export function AdminOrders() {
  const [status, setStatus] = useState("");
  const [tier, setTier] = useState("");
  const [offset, setOffset] = useState(0);

  const { data, loading, error, reload } = useAsync<Paged<AdminOrder>>(
    (s) =>
      listAdminOrders(
        {
          status: status as AdminOrderStatus | "",
          tier: tier as PaidTierId | "",
          limit: PAGE,
          offset,
        },
        s,
      ),
    [status, tier, offset],
  );

  if (loading && !data) return <Loading label="Memuat pesanan…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <PageHeader title="Pesanan" lede="Semua pesanan lintas pengguna. Jumlah = nilai beku saat order dibuat." />

      <FilterBar label="Filter pesanan">
        <FilterSelect
          label="Status"
          value={status}
          options={STATUS_OPTIONS}
          onChange={(v) => {
            setOffset(0);
            setStatus(v);
          }}
        />
        <FilterSelect
          label="Tier"
          value={tier}
          options={TIER_OPTIONS}
          onChange={(v) => {
            setOffset(0);
            setTier(v);
          }}
        />
      </FilterBar>

      {!data || data.items.length === 0 ? (
        <EmptyState title="Tidak ada pesanan">Tidak ada pesanan untuk filter ini.</EmptyState>
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          <AdminPanel>
            <DataTable label="Daftar pesanan" columns={COLUMNS} stackBelow="58rem" busy={loading}>
              {data.items.map((o) => (
                <DataRow key={o.id}>
                  <DataCell label="Waktu" className={dataCellClass}>
                    {formatDateTime(o.created_at)}
                  </DataCell>
                  <DataCell label="Pengguna" className="whitespace-nowrap">
                    <Link to={`/admin/users/${o.user_id}`}>{o.user_email ?? o.user_id}</Link>
                  </DataCell>
                  <DataCell label="Tier">{tierLabel(o.tier)}</DataCell>
                  {/* The coupon sits under the frozen amount it discounted; block
                      lines follow the cell's alignment (right in table layout). */}
                  <DataCell label="Jumlah" align="end" className="whitespace-nowrap">
                    <span className={cn(dataClass, "block text-fg-0")}>{formatRupiah(o.amount)}</span>
                    {o.coupon_code && (
                      <span
                        className="mt-0.5 block text-xs text-fg-2"
                        title={`Diskon ${formatRupiah((o.list_amount_minor ?? o.amount) - o.amount)}`}
                      >
                        Kupon <span className={dataClass}>{o.coupon_code}</span>
                      </span>
                    )}
                  </DataCell>
                  <DataCell label="Status">
                    <Badge tone={orderStatusTone(o.status)} dot>
                      {orderStatusLabel(o.status)}
                    </Badge>
                  </DataCell>
                  <DataCell label="Detail">
                    <Link to={`/admin/orders/${o.id}`}>
                      Lihat<span className="sr-only"> pesanan {formatDateTime(o.created_at)}</span>
                    </Link>
                  </DataCell>
                </DataRow>
              ))}
            </DataTable>
          </AdminPanel>
          <Pagination
            total={data.total}
            limit={data.limit}
            offset={data.offset}
            onPage={setOffset}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
}
